import React, { useMemo } from 'react';
import { Steps, Card, Typography, Button, Flex } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import './DocumentProgressSteps.css';

const { Text } = Typography;

/**
 * Vertical progress (Share Page-style) with Attach step; QR is a separate card below.
 */
const DocumentProgressSteps = ({
  variant,
  document,
  onDownloadPdf,
  downloading = false,
  layout = 'default',
}) => {
  const { items, current, stepStatus } = useMemo(() => {
    if (variant === 'invoice') {
      return buildInvoiceSteps(document, { onDownloadPdf, downloading });
    }
    return buildEstimateSteps(document, { onDownloadPdf, downloading });
  }, [variant, document, onDownloadPdf, downloading]);

  if (!items?.length) return null;

  return (
    <Card
      // className={`document-progress-steps-card${layout === 'sidebar' ? ' document-progress-steps-sidebar' : ''}`}
      className="shadow-none border-none"
      variant="borderless"
      size="small"
      styles={{ body: { padding: '16px 20px' } }}
    >
      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 12 }}>
        Progress
      </Text>
      <Steps
        orientation="vertical"
        size="small"
        status={stepStatus}
        current={current}
        items={items}
      />
    </Card>
  );
};

function actorSuffix(changedBy) {
  if (!changedBy) return '';
  const name =
    (changedBy.extra?.full_name && String(changedBy.extra.full_name).trim()) ||
    [changedBy.first_name, changedBy.last_name].filter(Boolean).join(' ');
  return name ? ` · ${name}` : '';
}

/** Uses API `extra.status_history.progress_steps` (organization timezone). */
function stepDescWithMeta(text, progressSteps, key) {
  if (!key || !progressSteps) return text;
  const p = progressSteps[key];
  if (!p?.formatted) return text;
  return (
    <Flex vertical gap={4}>
      <span>{text}</span>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {p.formatted}
        {actorSuffix(p.changed_by)}
      </Text>
    </Flex>
  );
}

function attachDescription(onDownloadPdf, downloading) {
  return (
    <Flex vertical gap={8} className="document-progress-steps-embed">
      <Text type="secondary" style={{ fontSize: 12 }}>
        Download and keep a PDF copy for your records.
      </Text>
      <Button
        type="primary"
        size="small"
        icon={<DownloadOutlined />}
        loading={downloading}
        onClick={onDownloadPdf}
        style={{ alignSelf: 'flex-start' }}
      >
        Download PDF
      </Button>
    </Flex>
  );
}

function buildInvoiceSteps(invoice, { onDownloadPdf, downloading }) {
  const payments = invoice.payments ?? [];
  const processedCount = payments.filter((p) => String(p.status).toLowerCase() === 'processed').length;
  const isPartials = processedCount > 1;

  // Backend split: `status` is the lifecycle envelope (active/on_hold/cancelled),
  // `status_workflow` is the workflow phase (draft/pending/confirmed/...).
  const envelopeCode = String(
    invoice.extra?.status_summary?.status?.code ?? invoice.status ?? '',
  ).toLowerCase();
  const workflowCode = String(
    invoice.extra?.status_summary?.status_workflow?.code ?? invoice.status_workflow ?? '',
  ).toLowerCase();
  const paymentCode = String(
    invoice.extra?.status_summary?.status_payment?.code ?? invoice.status_payment ?? '',
  ).toLowerCase();

  const isCancelled = envelopeCode === 'cancelled' || envelopeCode === 'canceled' || workflowCode === 'refunded';

  const progressSteps = invoice.extra?.status_history?.progress_steps;

  const items = [
    {
      title: 'Order placement',
      description: stepDescWithMeta('Your order was received and created', progressSteps, 'order_placement'),
    },
  ];

  if (isCancelled) {
    items.push({
      title: 'Cancelled',
      description: stepDescWithMeta('This invoice has been cancelled', progressSteps, 'cancelled'),
    });
    return { items, current: 1, stepStatus: 'error' };
  }

  if (workflowCode === 'draft') {
    items.push({
      title: 'Invoice',
      description: 'Awaiting confirmation',
    });
  } else {
    items.push({
      title: 'Invoiced',
      description: stepDescWithMeta('Your order was processed and invoiced', progressSteps, 'invoiced'),
    });
  }

  items.push({
    title: 'Attach',
    description: attachDescription(onDownloadPdf, downloading),
  });

  if (paymentCode === 'partial' || isPartials) {
    items.push({
      title: 'Partial payment',
      description: stepDescWithMeta('One or more payments received', progressSteps, 'partial_payment'),
    });
  }

  if (paymentCode === 'paid') {
    items.push({
      title: 'Paid',
      description: stepDescWithMeta('Payment completed', progressSteps, 'paid'),
    });
  } else {
    items.push({
      title: 'Payment',
      description: 'Awaiting full payment',
    });
  }

  let current = 0;
  let stepStatus = 'process';

  if (workflowCode === 'draft') {
    current = 1;
    stepStatus = 'process';
  } else if (paymentCode === 'paid') {
    current = items.length - 1;
    stepStatus = 'finish';
  } else {
    current = items.length - 1;
    stepStatus = 'process';
  }

  return { items, current, stepStatus };
}

function buildEstimateSteps(estimate, { onDownloadPdf, downloading }) {
  const statusCode = String(estimate.status ?? '').toLowerCase();
  const progressSteps = estimate.extra?.status_history?.progress_steps;

  const items = [
    {
      title: 'Estimate created',
      description: stepDescWithMeta('Your estimate was created', progressSteps, 'estimate_created'),
    },
  ];

  if (statusCode === 'cancelled' || statusCode === 'canceled') {
    items.push({
      title: 'Cancelled',
      description: stepDescWithMeta('This estimate has been cancelled', progressSteps, 'cancelled'),
    });
    return { items, current: 1, stepStatus: 'error' };
  }

  if (statusCode === 'draft') {
    items.push({ title: 'Estimate', description: 'Awaiting issue' });
  } else {
    items.push({
      title: 'Estimate',
      description: stepDescWithMeta('Your estimate is available', progressSteps, 'estimate_active'),
    });
  }

  items.push({
    title: 'Attach',
    description: attachDescription(onDownloadPdf, downloading),
  });

  if (statusCode === 'converted') {
    items.push({
      title: 'Converted',
      description: stepDescWithMeta('Converted to an invoice', progressSteps, 'converted'),
    });
    return { items, current: items.length - 1, stepStatus: 'finish' };
  }
  if (statusCode === 'expired') {
    items.push({
      title: 'Expired',
      description: stepDescWithMeta('This estimate has expired', progressSteps, 'expired'),
    });
    return { items, current: items.length - 1, stepStatus: 'error' };
  }
  if (statusCode === 'accepted') {
    items.push({
      title: 'Accepted',
      description: stepDescWithMeta('This estimate was accepted', progressSteps, 'accepted'),
    });
    return { items, current: items.length - 1, stepStatus: 'finish' };
  }
  if (statusCode === 'declined') {
    items.push({
      title: 'Declined',
      description: stepDescWithMeta('This estimate was declined', progressSteps, 'declined'),
    });
    return { items, current: items.length - 1, stepStatus: 'error' };
  }

  items.push({
    title: 'Response',
    description: 'Awaiting acceptance, decline, or conversion',
  });

  let current = 1;
  let stepStatus = 'process';
  if (statusCode === 'draft') {
    current = 1;
  } else {
    current = items.length - 1;
  }

  return { items, current, stepStatus };
}

export default DocumentProgressSteps;
